import { useCallback, useMemo } from "react";
import { FilterOptions } from "@sscs/types";
import { capitalizeFirstLetter } from "@sscs/utils";
import { useGetWhoamiQuery } from "Services";
import { AUDIT_FILTER_LABELS } from "../../constants";
import { useLazyGetAuditEnumsQuery } from "../../services/audit.api";
import { AUDIT_RESOURCE_TYPES, AuditFilters, ResourceType } from "../../types";

const MUTUALLY_EXCLUSIVE = "Mutually exclusive filter is already present";

const isFilterDisabled = ({
  name,
  filters,
}: {
  name: string;
  filters: AuditFilters;
}) => {
  const prerequisiteFiltersMapping: Record<string, string[]> = {
    resource_name: ["resource_type"],
  };

  const prerequisiteFilters = prerequisiteFiltersMapping[name];

  if (!prerequisiteFilters) return false;

  return !prerequisiteFilters.every((prerequisiteFilter) => {
    return Object.hasOwn(filters, prerequisiteFilter);
  });
};

export const useFilterOptions = ({
  filters,
}: {
  filters: AuditFilters;
}): FilterOptions<keyof AuditFilters> | null => {
  const isDisabled = useCallback(
    (name: keyof AuditFilters): boolean => isFilterDisabled({ name, filters }),
    [filters],
  );

  const [getAuditEnums, { data: auditMappings, isLoading: isEnumsLoading }] =
    useLazyGetAuditEnumsQuery();

  const whoami = useGetWhoamiQuery();

  return useMemo((): FilterOptions<keyof AuditFilters> | null => {
    return {
      action: {
        name: "action",
        label: AUDIT_FILTER_LABELS["action"],
        component: "autocomplete",
        operations: ["is", "is_any", "is_not", "is_not_any"],
        isLoading: isEnumsLoading,
        onFilterSelect: () => getAuditEnums(undefined, true),
        options: auditMappings?.actions,
        isOptionDisabled: isDisabled("action"),
        disabledReason: MUTUALLY_EXCLUSIVE,
      },
      outcome: {
        name: "outcome",
        label: AUDIT_FILTER_LABELS["outcome"],
        component: "autocomplete",
        operations: ["is"],
        options: ["SUCCESS", "FAILURE"],
        getOptionLabel: (option) => {
          return option === "SUCCESS" ? "Successful" : "Unsuccessful";
        },
        disabledReason: MUTUALLY_EXCLUSIVE,
      },
      user_email: {
        name: "user_email",
        label: AUDIT_FILTER_LABELS["user_email"],
        component: "input",
        operations: ["is"],
      },
      auth_context: {
        name: "auth_context",
        label: AUDIT_FILTER_LABELS["auth_context"],
        component: "autocomplete",
        operations: ["is"],
        options: ["SESSION", "TOKEN"],
        isOptionDisabled: isDisabled("auth_context"),
        disabledReason: MUTUALLY_EXCLUSIVE,
        getOptionLabel: (option: string): string =>
          capitalizeFirstLetter(option.toLowerCase()),
      },
      resource_type: {
        name: "resource_type",
        label: AUDIT_FILTER_LABELS["resource_type"],
        component: "autocomplete",
        operations: ["is"],
        isLoading: isEnumsLoading,
        options: Object.keys(AUDIT_RESOURCE_TYPES),
        isOptionDisabled: isDisabled("resource_type"),
        disabledReason: MUTUALLY_EXCLUSIVE,
        getOptionLabel: (option: ResourceType): string =>
          AUDIT_RESOURCE_TYPES[option],
      },
      resource_name: {
        name: "resource_name",
        label: AUDIT_FILTER_LABELS["resource_name"],
        component: "input",
        operations: ["is"],
        isOptionDisabled: isDisabled("resource_name"),
        disabledReason: "Select resource type to enable this filter",
        inputProps: { maxLength: 610 },
      },
      group_id: {
        name: "group_id",
        label: AUDIT_FILTER_LABELS["group_id"],
        component: "autocomplete",
        operations: ["is", "is_any"],
        isLoading: whoami.isLoading || whoami.isFetching,
        options: whoami.data?.org_groups.map((item) => item.group_id),
        getOptionLabel: (option) =>
          whoami.data?.org_groups.filter((item) => item.group_id === option)[0]
            .name ?? "",
        isOptionDisabled: isDisabled("group_id"),
        disabledReason: MUTUALLY_EXCLUSIVE,
      },
      project_name: {
        name: "project_name",
        label: AUDIT_FILTER_LABELS["project_name"],
        component: "input",
        operations: ["is"],
        inputProps: { maxLength: 200 },
      },
      package_name: {
        name: "package_name",
        label: AUDIT_FILTER_LABELS["package_name"],
        component: "input",
        operations: ["is"],
        inputProps: { maxLength: 200 },
      },
    };
  }, [isDisabled, whoami, isEnumsLoading, auditMappings, getAuditEnums]);
};
